import React, { useState, useEffect } from "react";
import { Button } from "../ui/components/Button";
import { Badge } from "../ui/components/Badge";
import { Table } from "../ui/components/Table";
import { Loader } from "../ui/components/Loader";
import { FeatherX, FeatherRefreshCw, FeatherDownload, FeatherTrash2 } from "@subframe/core";
import { gstr2bAPI, UploadLogEntry } from "../services/gstr2bApi";

interface UploadLogModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
};

const formatReturnPeriod = (period: string): string => {
  if (!period || period.length < 6) return period;
  const month = period.substring(0, 2);
  const year = period.substring(2, 6);
  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const m = parseInt(month, 10);
  const name = monthNames[m - 1] || month;
  return `${name} ${year}`;
};

const formatCurrency = (amount: string | number): string => {
  const n = typeof amount === "string" ? parseFloat(amount) : amount;
  if (Number.isNaN(n)) return "—";
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const getStatusBadge = (status: UploadLogEntry["status"]) => {
  switch (status) {
    case "completed":
      return { variant: "success" as const, label: "Completed" };
    case "failed":
      return { variant: "error" as const, label: "Failed" };
    case "processing":
    default:
      return { variant: "warning" as const, label: "Processing" };
  }
};

const UploadLogModal: React.FC<UploadLogModalProps> = ({ isOpen, onClose }) => {
  const [uploads, setUploads] = useState<UploadLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [resettingId, setResettingId] = useState<number | null>(null);

  const handleResetPeriod = async (row: UploadLogEntry) => {
    const confirmed = window.confirm(
      `Reset ${formatReturnPeriod(row.return_period)}?\n\n` +
      `This will delete ${row.total_invoices} GSTR-2B invoice${row.total_invoices === 1 ? '' : 's'} ` +
      `and un-reconcile any matched purchase register entries.\n\n` +
      `You can re-upload the same file afterwards.\n\n` +
      `Your purchase register data will NOT be affected.`
    );
    if (!confirmed) return;

    try {
      setResettingId(row.id);
      const response = await gstr2bAPI.resetUploadPeriod(row.id);
      if (response.success && response.data) {
        alert(
          `${formatReturnPeriod(response.data.return_period)} reset successfully.\n` +
          `${response.data.invoices_deleted} invoice${response.data.invoices_deleted === 1 ? '' : 's'} deleted.\n\n` +
          `You can now re-import the file.`
        );
        await fetchUploads();
      } else {
        alert(response.message || 'Failed to reset period');
      }
    } catch (err: any) {
      alert(err.message || 'Failed to reset period');
    } finally {
      setResettingId(null);
    }
  };

  const handleDownload = async (uploadId: number) => {
    try {
      setDownloadingId(uploadId);
      const response = await gstr2bAPI.getDownloadUrl(uploadId);
      if (response.success && response.data?.download_url) {
        window.open(response.data.download_url, "_blank");
      } else {
        alert(response.message || "Failed to get download URL");
      }
    } catch (err: any) {
      alert(err.message || "Failed to download file");
    } finally {
      setDownloadingId(null);
    }
  };

  const fetchUploads = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await gstr2bAPI.getUploadLog();
      if (response.success && response.data?.uploads) {
        setUploads(response.data.uploads);
      } else {
        setError(response.message || "Failed to load upload log");
      }
    } catch (err: any) {
      setError(err.message || "Failed to load upload log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchUploads();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
        onClick={onClose}
      >
        <div
          className="mx-4 flex max-h-[90vh] w-full max-w-5xl flex-col rounded-lg border border-solid border-neutral-border bg-default-background shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-solid border-neutral-border px-6 py-4">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Upload Log
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="neutral-tertiary"
                size="small"
                icon={<FeatherRefreshCw />}
                onClick={fetchUploads}
                disabled={loading}
              >
                Refresh
              </Button>
              <button
                type="button"
                onClick={onClose}
                className="text-subtext-color transition-colors hover:text-default-font"
              >
                <FeatherX className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-auto px-6 py-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader />
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center gap-3 py-12">
                <span className="text-body font-body text-error-600">
                  {error}
                </span>
                <Button variant="neutral-secondary" onClick={fetchUploads}>
                  Retry
                </Button>
              </div>
            ) : uploads.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <span className="text-body font-body text-neutral-500">
                  No uploads yet. Import a GSTR-2B JSON file to see history here.
                </span>
              </div>
            ) : (
              <Table
                header={
                  <Table.HeaderRow>
                    <Table.HeaderCell>Upload Date</Table.HeaderCell>
                    <Table.HeaderCell>File Name</Table.HeaderCell>
                    <Table.HeaderCell>Return Period</Table.HeaderCell>
                    <Table.HeaderCell>GSTIN</Table.HeaderCell>
                    <Table.HeaderCell>Total Invoices</Table.HeaderCell>
                    <Table.HeaderCell>New</Table.HeaderCell>
                    <Table.HeaderCell>Duplicates</Table.HeaderCell>
                    <Table.HeaderCell>Total ITC</Table.HeaderCell>
                    <Table.HeaderCell>Status</Table.HeaderCell>
                    <Table.HeaderCell>File</Table.HeaderCell>
                    <Table.HeaderCell>Error</Table.HeaderCell>
                    <Table.HeaderCell>Reset</Table.HeaderCell>
                  </Table.HeaderRow>
                }
              >
                {uploads.map((row) => {
                  const statusBadge = getStatusBadge(row.status);
                  return (
                    <Table.Row key={row.id}>
                      <Table.Cell>
                        <span className="whitespace-nowrap text-body font-body text-default-font">
                          {formatDate(row.upload_date)}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="max-w-[180px] truncate text-body font-body text-default-font" title={row.file_name || undefined}>
                          {row.file_name || "—"}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="text-body font-body text-default-font">
                          {formatReturnPeriod(row.return_period)}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="text-body font-body text-default-font">
                          {row.gstin || "—"}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="text-body font-body text-default-font">
                          {row.total_invoices.toLocaleString("en-IN")}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="text-body font-body text-default-font">
                          {row.new_invoices.toLocaleString("en-IN")}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="text-body font-body text-default-font">
                          {row.duplicate_invoices.toLocaleString("en-IN")}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="text-body font-body text-default-font">
                          {formatCurrency(row.total_itc_amount)}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge variant={statusBadge.variant}>
                          {statusBadge.label}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        {row.s3_key ? (
                          <Button
                            variant="neutral-tertiary"
                            size="small"
                            icon={<FeatherDownload />}
                            onClick={() => handleDownload(row.id)}
                            loading={downloadingId === row.id}
                            disabled={downloadingId === row.id}
                          >
                            Download
                          </Button>
                        ) : (
                          <span className="text-caption font-caption text-neutral-400">—</span>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <span className="max-w-[140px] truncate text-caption font-caption text-error-600" title={row.error_message || undefined}>
                          {row.status === "failed" && row.error_message ? row.error_message : "—"}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          variant="destructive-tertiary"
                          size="small"
                          icon={<FeatherTrash2 />}
                          onClick={() => handleResetPeriod(row)}
                          loading={resettingId === row.id}
                          disabled={resettingId !== null}
                        >
                          Reset
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default UploadLogModal;
